<template>
    <div>
        <TooltipHeader text="Stempelinformatie" />
        <p>
            Stempel van {{ stamp.iterationLength }} dagen, herhalend vanaf {{ beginDateFormatted }}
            <template v-if="stamp.endDate">
                t/m {{ endDateFormatted }}
            </template>
        </p>
        <p>
            Huidig bekeken herhaling duurt van {{ iterationStartDate }} t/m {{ iterationEndDate }}
        </p>
        <template v-if="personalStampShifts.length > 0">
            <StampTooltipShift
                v-for="personalStampShift in personalStampShifts"
                :key="personalStampShift.id"
                :stampShift="personalStampShift"
                :template="shifts[personalStampShift.shiftId].template!"
                :shifts="shifts"
                :rosters="rosters"
                :rosterId="rosterId"
            />
        </template>
    </div>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import TooltipHeader from '@/components/Tooltip/TooltipHeader.vue';
    import StampTooltipShift from '@/components/Tooltip/StampTooltipShift.vue';
    import Dates from '@cquential/date-utils';
    import {PersonalStampShift, Shift, PersonalStamp} from '@cquential/api';
    import {RosterSelectOption} from '@/types/types';

    @Component({
        components: {
            TooltipHeader,
            StampTooltipShift,
        },
    })
    export default class StampTooltip extends Vue {
        @Prop({required: true}) public stamp!: PersonalStamp;
        @Prop({required: true}) public date!: Date;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public rosters!: RosterSelectOption[];
        @Prop({required: true}) public rosterId!: number;

        public get iterationStartDate(): string {
            const daysOfPreviousIterationsPassed: number = Math.floor(this.amountOfDaysPassedSinceBeginDate / this.stamp.iterationLength) * this.stamp.iterationLength;
            const iterationStartDate: Date = Dates.Utils.addDays(this.stamp.beginDate, daysOfPreviousIterationsPassed);

            return Dates.Formatter.formatDateHumanFriendly(iterationStartDate);
        }

        public get iterationEndDate(): string {
            const daysPassedWhenThisIterationIsEntirelyOver: number = (1 + Math.floor(this.amountOfDaysPassedSinceBeginDate / this.stamp.iterationLength)) * this.stamp.iterationLength;
            let iterationEndDate: Date = Dates.Utils.addDays(this.stamp.beginDate, daysPassedWhenThisIterationIsEntirelyOver - 1);

            if (this.stamp.endDate && iterationEndDate > this.stamp.endDate) {
                iterationEndDate = this.stamp.endDate;
            }

            return Dates.Formatter.formatDateHumanFriendly(iterationEndDate);
        }

        public get beginDateFormatted(): string {
            return Dates.Formatter.formatDateHumanFriendly(this.stamp.beginDate);
        }

        public get endDateFormatted(): string {
            return this.stamp.endDate ? Dates.Formatter.formatDateHumanFriendly(this.stamp.endDate) : '';
        }

        public get amountOfDaysPassedSinceBeginDate(): number {
            return Dates.Utils.diffInDays(this.date, this.stamp.beginDate);
        }

        public get personalStampShifts(): PersonalStampShift[] {
            if (!this.stamp.personalStampShifts || this.stamp.personalStampShifts.length === 0) {
                return [];
            }

            const dayIndex: number = Dates.Utils.diffInDays(this.date, this.stamp.beginDate) % this.stamp.iterationLength;

            return this.stamp.personalStampShifts.filter((shift: PersonalStampShift): boolean => shift.dayIndex === dayIndex);
        }
    }
</script>
